import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, ArrowRight, CheckCircle, AlertCircle, Loader2, Lock, Key } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4 },
};

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('sender');
  const [loginMode, setLoginMode] = useState('magic'); // 'magic' or 'password'
  const [status, setStatus] = useState('idle'); // idle, sending, sent, verifying, error
  const [error, setError] = useState('');
  
  const { sendMagicLink, verifyMagicLink, loginWithPassword, isAuthenticated, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const redirectTo = searchParams.get('redirect') || '/app/sender';
  const tokenFromUrl = searchParams.get('token');

  // Auto-verify if token in URL
  useEffect(() => {
    if (tokenFromUrl && status === 'idle') {
      handleVerify(tokenFromUrl);
    }
  }, [tokenFromUrl]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      const dashboardMap = {
        sender: '/app/sender',
        merchant: '/app/merchant',
        admin: '/app/admin',
      };
      navigate(dashboardMap[user.role] || redirectTo);
    }
  }, [isAuthenticated, user, navigate, redirectTo]);

  const handleSendMagicLink = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setError('');

    try {
      await sendMagicLink(email, role);
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setError(err.message);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setError('');

    try {
      await loginWithPassword(email, password);
      // Navigation handled by useEffect
    } catch (err) {
      setStatus('error');
      setError(err.message || 'Invalid email or password');
    }
  };

  const handleVerify = async (token) => {
    setStatus('verifying');
    setError('');

    try {
      await verifyMagicLink(token);
      // Navigation handled by useEffect
    } catch (err) {
      setStatus('error');
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-blue-900 flex items-center justify-center p-6">
      <motion.div
        initial="initial"
        animate="animate"
        variants={fadeInUp}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
            <span className="text-emerald-700 font-bold text-xl">P</span>
          </div>
          <span className="font-bold text-2xl text-white">PeCashGlobal</span>
        </Link>

        {/* Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8" data-testid="login-card">
          {status === 'verifying' ? (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 text-emerald-600 animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">{t('login.verifying')}</h2>
              <p className="text-slate-600">{t('login.verifyingSubtext')}</p>
            </div>
          ) : status === 'sent' ? (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">{t('login.emailSent')}</h2>
              <p className="text-slate-600 mb-6">
                {t('login.emailSentText')} <strong>{email}</strong>.
              </p>
              <p className="text-sm text-slate-500 mb-4">
                {t('login.emailSentExpiry')}
              </p>
              
              <button
                onClick={() => setStatus('idle')}
                className="mt-4 text-sm text-emerald-700 hover:underline"
              >
                {t('login.differentEmail')}
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-slate-900 text-center mb-2">
                {t('login.title')}
              </h2>
              <p className="text-slate-600 text-center mb-6">
                {loginMode === 'magic' ? t('login.subtitle') : 'Enter your password to sign in'}
              </p>

              {/* Login Mode Toggle */}
              <div className="flex rounded-xl bg-slate-100 p-1 mb-6">
                <button
                  type="button"
                  onClick={() => { setLoginMode('magic'); setError(''); }}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    loginMode === 'magic'
                      ? 'bg-white text-emerald-700 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid="mode-magic"
                >
                  <Mail className="w-4 h-4" />
                  Magic Link
                </button>
                <button
                  type="button"
                  onClick={() => { setLoginMode('password'); setError(''); }}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    loginMode === 'password'
                      ? 'bg-white text-emerald-700 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid="mode-password"
                >
                  <Lock className="w-4 h-4" />
                  Password
                </button>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <form onSubmit={loginMode === 'magic' ? handleSendMagicLink : handlePasswordLogin}>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="email" className="text-slate-700">{t('login.emailLabel')}</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t('login.emailPlaceholder')}
                        required
                        data-testid="email-input"
                        className="pl-10 h-12 rounded-xl"
                      />
                    </div>
                  </div>

                  {loginMode === 'password' && (
                    <div>
                      <Label htmlFor="password" className="text-slate-700">Password</Label>
                      <div className="relative mt-1">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="password"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your password"
                          required
                          data-testid="password-input"
                          className="pl-10 h-12 rounded-xl"
                        />
                      </div>
                    </div>
                  )}

                  {loginMode === 'magic' && (
                    <div>
                      <Label className="text-slate-700">{t('login.roleLabel')}</Label>
                      <div className="grid grid-cols-2 gap-2 mt-1">
                        {[
                          { key: 'sender', label: t('login.roleSender') },
                          { key: 'merchant', label: t('login.roleMerchant') }
                        ].map((r) => (
                          <button
                            key={r.key}
                            type="button"
                            onClick={() => setRole(r.key)}
                            data-testid={`role-${r.key}`}
                            className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                              role === r.key
                                ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-600'
                                : 'bg-slate-100 text-slate-600 border-2 border-transparent hover:bg-slate-200'
                            }`}
                          >
                            {r.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={status === 'sending'}
                    data-testid={loginMode === 'magic' ? 'send-magic-link-btn' : 'password-login-btn'}
                    className="w-full h-12 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold mt-4"
                  >
                    {status === 'sending' ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        {loginMode === 'magic' ? t('login.sendButton') : 'Sign In'}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </form>

              <p className="text-center text-sm text-slate-500 mt-6">
                {t('login.termsText')}{' '}
                <Link to="/terms" className="text-emerald-700 hover:underline">{t('login.termsLink')}</Link>
                {' '}{t('login.andText')}{' '}
                <Link to="/privacy" className="text-emerald-700 hover:underline">{t('login.privacyLink')}</Link>
              </p>
            </>
          )}
        </div>

        <p className="text-center text-emerald-100 mt-6">
          <Link to="/" className="hover:underline">{t('login.backHome')}</Link>
        </p>
      </motion.div>
    </div>
  );
}
